import { useMemo } from 'react';
import {
  CHANGE_CLASSES,
  DIFF_STYLE,
  type ChangeClass,
  type DiffEntry,
  type DiffResult,
} from '@drawing2d/core';
import { t, fill, type Lang } from '../i18n.js';

/**
 * What changed, in words, as a list you can walk.
 *
 * The comparison surface had counts and it had marks, and nothing in between.
 * Testing it produced "the compare interface confuses me too much", which was
 * fair: sixty marks in six line types over a drawing, a legend, and twenty rows
 * of matcher profile and tolerance figures — and nowhere an answer to the
 * question a reviewer actually arrives with, which is *what changed*. Finding a
 * change meant hunting the sheet for a mark whose dash pattern you had first
 * looked up in the legend.
 *
 * So the changes are enumerated. One row each, named by class and entity type,
 * ordered so the decisions come before the things left undecided, with the
 * unchanged left out entirely — a list of everything that stayed the same is a
 * list nobody reads. Clicking a row selects the change, which is the same
 * selection clicking the mark makes, so the list and the drawing drive each
 * other.
 */

/** `unchanged` is deliberately absent: it is the answer to a question nobody asked. */
const LISTED: readonly ChangeClass[] = CHANGE_CLASSES.filter((c) => c !== 'unchanged');

/** Decisions first, then what the matcher declined to decide. */
const ORDER: Record<string, number> = { added: 0, removed: 1, changed: 2, moved: 3, ambiguous: 4 };

export function ChangeList({
  diff,
  sheetIndex,
  lang,
  selected,
  onSelect,
}: {
  diff: DiffResult;
  sheetIndex: number;
  lang: Lang;
  selected: number | null;
  onSelect: (index: number | null) => void;
}): JSX.Element {
  const s = (k: Parameters<typeof t>[0]): string => t(k, lang);

  /**
   * Indices into `diff.entries`, never entity identifiers — both versions contain
   * an entity called `p0-1` and they are different entities, so a list keyed on
   * the identifier would step the reviewer onto the wrong side of the wrong
   * change while looking entirely correct.
   */
  const rows = useMemo(() => {
    const out: { index: number; entry: DiffEntry }[] = [];
    for (const [index, entry] of diff.entries.entries()) {
      if (entry.sheetIndex !== sheetIndex) continue;
      if (!LISTED.includes(entry.changeClass)) continue;
      out.push({ index, entry });
    }
    out.sort(
      (a, b) =>
        (ORDER[a.entry.changeClass] ?? 9) - (ORDER[b.entry.changeClass] ?? 9) ||
        a.entry.entityType.localeCompare(b.entry.entityType) ||
        a.index - b.index,
    );
    return out;
  }, [diff.entries, sheetIndex]);

  const position = rows.findIndex((r) => r.index === selected);

  const step = (by: number): void => {
    if (rows.length === 0) return;
    const next = position < 0 ? 0 : (position + by + rows.length) % rows.length;
    onSelect(rows[next]!.index);
  };

  return (
    <section className="panel change-list">
      <h2>
        {s('changesTitle')}
        <span className="fold-count">
          {rows.length === 0
            ? s('noChanges')
            : position < 0
              ? fill(s('changesCount'), { n: rows.length })
              : fill(s('changesPosition'), { i: position + 1, n: rows.length })}
        </span>
      </h2>

      {rows.length > 0 && (
        <div className="stepper">
          <button onClick={() => step(-1)}>{s('previousChange')}</button>
          <button onClick={() => step(1)}>{s('nextChange')}</button>
          {selected !== null && (
            <button className="link" onClick={() => onSelect(null)}>
              {s('clearSelection')}
            </button>
          )}
        </div>
      )}

      {rows.length === 0 ? (
        <p className="note">{s('noChangesNote')}</p>
      ) : (
        <ul className="changes">
          {rows.map(({ index, entry }) => (
            <li key={index}>
              <button
                data-change-row={index}
                data-change-class={entry.changeClass}
                className={index === selected ? 'on' : ''}
                onClick={() => onSelect(index)}
              >
                <span className="row-class">{DIFF_STYLE[entry.changeClass].label[lang]}</span>
                <span className="row-type">{entry.entityType}</span>
                <span className="row-where">{whereOf(entry, diff, lang)}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

/**
 * Where the change is, said in the terms the reviewer can act on.
 *
 * A displacement is given in the unit the result declares, which is millimetres
 * only when scale was resolved — page diagonals otherwise. Labelling normalised
 * units as millimetres would be a wrong measurement stated confidently.
 */
function whereOf(entry: DiffEntry, diff: DiffResult, lang: Lang): string {
  if (entry.changeClass === 'moved' && entry.displacement !== undefined) {
    return fill(t('movedBy', lang), {
      d: entry.displacement.toFixed(2),
      unit: unitWord(diff.displacementUnit, lang),
    });
  }
  if (entry.changeClass === 'ambiguous') {
    return fill(t('rivalCount', lang), { n: entry.candidates?.length ?? 0 });
  }
  if (entry.detail) return entry.detail;
  return '';
}

function unitWord(unit: string, lang: Lang): string {
  return unit === 'mm' ? 'mm' : t('unitPageDiagonal', lang);
}
